import { apiFetch, jsonBody } from "./api";

export type User = {
  id?: string;
  email: string;
  role?: "admin" | "user" | "owner" | string;
  displayName?: string;
  agencyRole?: "manager" | "operator" | string;
  permissions?: string[];
  active?: boolean;
  onboardingCompleted?: boolean;
};

export type Agency = {
  id?: string;
  userId?: string;
  name?: string;
  legalName?: string;
  cif?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  plan?: string;
  discountType?: "percent" | "amount";
  discountValue?: number;
  status?: "active" | "pending" | "suspended" | string;
  onboardingCompleted?: boolean;
  billingSubscriptionStatus?: string;
  billingCurrentPeriodEnd?: string;
  billingCancelAtPeriodEnd?: boolean;
};

export type AuthResponse = {
  token: string;
  user: User;
  agency?: Agency | null;
};

const TOKEN_KEY = "vilatio_token";
const USER_KEY = "vilatio_user";
const AGENCY_KEY = "vilatio_agency";

function setCookie(name: string, value: string, maxAge = 60 * 60 * 24 * 30) {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax${secure}`;
}

function deleteCookie(name: string) {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax${secure}`;
}

export function hasCompletedOnboarding(user?: User | null, agency?: Agency | null) {
  return Boolean(user?.onboardingCompleted || agency?.onboardingCompleted || agency);
}

export function saveSession(session: AuthResponse) {
  window.localStorage.setItem(TOKEN_KEY, session.token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(session.user));

  if (session.agency) {
    window.localStorage.setItem(AGENCY_KEY, JSON.stringify(session.agency));
  } else {
    window.localStorage.removeItem(AGENCY_KEY);
  }

  setCookie("vilatio_token", session.token);
  setCookie("vilatio_role", session.user.role ?? "user");
  setCookie(
    "vilatio_onboarding",
    hasCompletedOnboarding(session.user, session.agency) ? "true" : "false",
  );
}

export function updateStoredAgency(agency: Agency) {
  window.localStorage.setItem(AGENCY_KEY, JSON.stringify(agency));
  setCookie("vilatio_onboarding", "true");

  const user = getStoredUser();
  if (user) {
    const updatedUser = { ...user, onboardingCompleted: true };
    window.localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  }
}

export function getStoredUser(): User | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawUser = window.localStorage.getItem(USER_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as User;
  } catch {
    return null;
  }
}

export function getStoredAgency(): Agency | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawAgency = window.localStorage.getItem(AGENCY_KEY);
  if (!rawAgency) {
    return null;
  }

  try {
    return JSON.parse(rawAgency) as Agency;
  } catch {
    return null;
  }
}

export async function login(email: string, password: string) {
  const session = await apiFetch<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: jsonBody({ email, password }),
  });
  saveSession(session);
  return session;
}

export async function register(email: string, password: string) {
  const session = await apiFetch<AuthResponse>("/api/auth/register", {
    method: "POST",
    body: jsonBody({ email, password }),
  });
  saveSession(session);
  return session;
}

export async function getUser() {
  const session = await apiFetch<AuthResponse>("/api/auth/me", {
    method: "GET",
  });
  saveSession({
    token: window.localStorage.getItem(TOKEN_KEY) ?? "",
    user: session.user,
    agency: session.agency,
  });
  return session;
}

export function logout() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
  window.localStorage.removeItem(AGENCY_KEY);
  deleteCookie("vilatio_token");
  deleteCookie("vilatio_role");
  deleteCookie("vilatio_onboarding");
  // Change the URL before a hard reload so the browser re-enters through the
  // public login route after the client session has been removed.
  window.history.replaceState(null, "", "/login");
  window.location.reload();
}
